import { pgTable, text, boolean, timestamp, integer, index, uniqueIndex, foreignKey } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { miniBannerTargetType } from './enums';
import { file } from './files';

/**
 * MiniBanner — небольшой баннер, показываемый в просмотре урока между контентом и блоком "Следующий урок".
 *
 * Отличия от обычного Banner (главная страница):
 *   - два adaptive-слота вместо трёх: 19:2 (десктоп, обязательный) и
 *     21:9 (мобильный, опциональный — на мобилке fallback на 19:2);
 *   - у каждого баннера есть список "целей" (MiniBannerTarget) — где именно он показывается:
 *     на всём курсе, на конкретном модуле, на конкретном уроке или на трансляции.
 *
 * Локализация изображений — через MiniBannerImage.languageCode (nullable = универсальная).
 * Локализация текстов (title/subtitle/button) — через универсальную таблицу Translation
 * с entityType = 'MINI_BANNER'.
 */
export const miniBanner = pgTable('MiniBanner', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  linkUrl: text(),
  order: integer().default(0).notNull(),
  startDate: timestamp({ precision: 3, mode: 'string' }),
  endDate: timestamp({ precision: 3, mode: 'string' }),
  isActive: boolean().default(true).notNull(),
  viewsCount: integer().default(0).notNull(),
  clicksCount: integer().default(0).notNull(),
  createdAt: timestamp({ precision: 3, mode: 'string' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'string' }).notNull(),
}, (table) => [
  index('MiniBanner_isActive_idx').using('btree', table.isActive.asc().nullsLast()),
  index('MiniBanner_order_idx').using('btree', table.order.asc().nullsLast()),
  index('MiniBanner_startDate_idx').using('btree', table.startDate.asc().nullsLast()),
  index('MiniBanner_endDate_idx').using('btree', table.endDate.asc().nullsLast()),
]);

export const miniBannerImage = pgTable('MiniBannerImage', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  miniBannerId: integer().notNull(),
  imageId: integer().notNull(),
  /**
   * Соотношение сторон слота: '19:2' — десктоп (по умолчанию для обратной
   * совместимости со старыми записями), '21:9' — мобильный.
   * Формат с двоеточием — как у BannerImage.aspectRatio (единый паттерн).
   */
  aspectRatio: text().default('19:2').notNull(),
  languageCode: text(),
  createdAt: timestamp({ precision: 3, mode: 'string' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'string' }).notNull(),
}, (table) => [
  index('MiniBannerImage_miniBannerId_idx').using('btree', table.miniBannerId.asc().nullsLast()),
  index('MiniBannerImage_imageId_idx').using('btree', table.imageId.asc().nullsLast()),
  index('MiniBannerImage_languageCode_idx').using('btree', table.languageCode.asc().nullsLast()),
  index('MiniBannerImage_aspectRatio_idx').using('btree', table.aspectRatio.asc().nullsLast()),
  uniqueIndex('MiniBannerImage_miniBannerId_languageCode_aspectRatio_key').using(
    'btree',
    table.miniBannerId.asc().nullsLast(),
    table.languageCode.asc().nullsLast(),
    table.aspectRatio.asc().nullsLast(),
  ),
  foreignKey({
    columns: [table.miniBannerId],
    foreignColumns: [miniBanner.id],
    name: 'MiniBannerImage_miniBannerId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.imageId],
    foreignColumns: [file.id],
    name: 'MiniBannerImage_imageId_fkey',
  }).onUpdate('cascade').onDelete('restrict'),
]);

/**
 * MiniBannerTarget — к чему привязан баннер.
 *   - targetType = 'COURSE' → targetId ссылается на Course.id
 *   - targetType = 'MODULE' → targetId ссылается на Module.id
 *   - targetType = 'MODULE_ITEM' → targetId ссылается на ModuleItem.id
 *   - targetType = 'LIVESTREAM' → targetId ссылается на Livestream.id
 *
 * Один баннер может иметь несколько целей. Например, "на всём курсе 12 и на уроке 345".
 */
export const miniBannerTarget = pgTable('MiniBannerTarget', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  miniBannerId: integer().notNull(),
  targetType: miniBannerTargetType().notNull(),
  targetId: integer().notNull(),
  createdAt: timestamp({ precision: 3, mode: 'string' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  index('MiniBannerTarget_miniBannerId_idx').using('btree', table.miniBannerId.asc().nullsLast()),
  index('MiniBannerTarget_targetType_targetId_idx').using(
    'btree',
    table.targetType.asc().nullsLast(),
    table.targetId.asc().nullsLast(),
  ),
  uniqueIndex('MiniBannerTarget_miniBannerId_targetType_targetId_key').using(
    'btree',
    table.miniBannerId.asc().nullsLast(),
    table.targetType.asc().nullsLast(),
    table.targetId.asc().nullsLast(),
  ),
  foreignKey({
    columns: [table.miniBannerId],
    foreignColumns: [miniBanner.id],
    name: 'MiniBannerTarget_miniBannerId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
]);

export const miniBannerView = pgTable('MiniBannerView', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  miniBannerId: integer().notNull(),
  userId: integer(),
  createdAt: timestamp({ precision: 3, mode: 'string' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  index('MiniBannerView_miniBannerId_idx').using('btree', table.miniBannerId.asc().nullsLast()),
  index('MiniBannerView_userId_idx').using('btree', table.userId.asc().nullsLast()),
  index('MiniBannerView_createdAt_idx').using('btree', table.createdAt.asc().nullsLast()),
  foreignKey({
    columns: [table.miniBannerId],
    foreignColumns: [miniBanner.id],
    name: 'MiniBannerView_miniBannerId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
]);

export const miniBannerClick = pgTable('MiniBannerClick', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  miniBannerId: integer().notNull(),
  userId: integer(),
  createdAt: timestamp({ precision: 3, mode: 'string' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  index('MiniBannerClick_miniBannerId_idx').using('btree', table.miniBannerId.asc().nullsLast()),
  index('MiniBannerClick_userId_idx').using('btree', table.userId.asc().nullsLast()),
  index('MiniBannerClick_createdAt_idx').using('btree', table.createdAt.asc().nullsLast()),
  foreignKey({
    columns: [table.miniBannerId],
    foreignColumns: [miniBanner.id],
    name: 'MiniBannerClick_miniBannerId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
]);
